import React from 'react';
import PropTypes from 'prop-types';

import {sortByTextValue} from '@utils/sorters';
import {IconEdit} from '@appComponents/IconCollection';
import Table from '@appComponents/Table';
import {EVENT_TYPE_SPORTRADAR, EVENT_TYPE_NON_SPORTRADAR, SECURITY_PRIVILEGES_READ} from '@constants/variables';
import * as RESOURCES from '@constants/resources';
import LinkWithAuthorization from '@appComponents/LinkWithAuthorization';
import {getLink} from '@appComponents/Link';
import Authorization from '@appComponents/Authorization';

const authorizationObject = {
    [RESOURCES.EVENT_CONTENT_TYPE]: SECURITY_PRIVILEGES_READ,
};

export const columns = {
    'id': {
        label: 'ID',
        sorting: (row) => parseInt(row.id, 10),
        header: {
            align: 'center',
            isCollapsed: true,
        },
        content: {
            align: 'center',
        },
    },
    'name': {label: 'Name', sorting: true},
    'distribution_types': {
        label: 'Distribution types',
        sorting: row => getListRendererSortValue(row, 'distribution_types'),
    },
    'products': {
        label: 'Products',
        sorting: row => getListRendererSortValue(row, 'products', null, true),
    },
    'event_types': {
        label: 'Event types',
        sorting: row => getListRendererSortValue(row, 'event_types'),
    },
    'sports': {
        label: 'Sports',
        sorting: row => {
            const nonEmpty = Boolean(row.event_types.find(event_type => event_type.id === SPORTRADAR));

            return getListRendererSortValue(row, 'sports', nonEmpty);
        },
    },
    'content_categories': {
        label: '1st level categories',
        sorting: row => {
            const nonEmpty = Boolean(row.event_types.find(event_type => event_type.id === NON_SPORTRADAR));

            return getListRendererSortValue(row, 'content_categories', nonEmpty);
        },
    },
    'actions': {
        label: 'Actions',
        header: {
            align: 'center',
        },
        content: {
            align: 'center',
        },
    },
};

export const listRenderer = (row, column, nonEmpty = null, hasShortName = false) => {
    let contentToRender;

    if (false === nonEmpty) {
        contentToRender = '-';
    } else if (true === nonEmpty && 0 === row[column].length) {
        contentToRender = 'All';
    } else if (hasShortName) {
        contentToRender = (!row[column] || !row[column].length)
            ? '-' : sortByTextValue(row[column], ['short_name']).map((col => (col.short_name))).join(', ');
    } else {
        contentToRender = (!row[column] || !row[column].length)
            ? '-' : row[column].map((col => (col.name))).join(', ');
    }

    return contentToRender;
};

const getListRendererSortValue = (row, column, nonEmpty = null, hasShortName = false) => {
    const contentToRender = listRenderer(row, column, nonEmpty, hasShortName);

    return ('-' === contentToRender) ? null : contentToRender;
};

const SPORTRADAR = EVENT_TYPE_SPORTRADAR.toString();
const NON_SPORTRADAR = EVENT_TYPE_NON_SPORTRADAR.toString();

export const rowRenderer = (column, row) => {
    switch (column) {
        case 'id':
        case 'name':
            return (
                <LinkWithAuthorization
                    authorization={authorizationObject}
                    link={{
                        name: 'eventContentTypes.edit',
                        params: {id: row.id},
                    }}
                >
                    {row[column]}
                </LinkWithAuthorization>
            );
        case 'distribution_types' :
            return listRenderer(row, column);
        case 'products' :
            return listRenderer(row, column, null, true);
        case 'event_types' :
            return listRenderer(row, column);
        case 'sports' : {
            const nonEmpty = Boolean(row.event_types.find(event_type => event_type.id === SPORTRADAR));

            return listRenderer(row, column, nonEmpty);
        }
        case 'content_categories' : {
            const nonEmpty = Boolean(row.event_types.find(event_type => event_type.id === NON_SPORTRADAR));

            return listRenderer(row, column, nonEmpty);
        }
        case 'actions' :
            return (
                <Authorization authorization={authorizationObject}>
                    <div className='actions-list-icon'>
                        <IconEdit size='large' link={
                            getLink('eventContentTypes.edit', {id: row.id})
                        } resource={RESOURCES.EVENT_CONTENT_TYPE}/>
                    </div>
                </Authorization>
            );
        default:
            return null;
    }
};

const EventContentTypesTable = (props) => {
    const {types, loading} = props;

    return (
        <div className='types__table'>
            <Table
                columns={columns}
                data={types}
                loading={loading}
                name='typesList'
                noDataText='No event content types found'
                rowRenderer={rowRenderer}
                defaultSort={'name'}
            />
        </div>
    );
};

EventContentTypesTable.propTypes = {
    loading: PropTypes.bool,
    types: PropTypes.array,
};

EventContentTypesTable.defaultProps = {
    loading: false,
    types: [],
};

export default EventContentTypesTable;
